import useIntersection from '@/hooks/useIntersection';
import styles from './CountryFlag.module.scss';

interface CountryFlagProps {
  /** ISO 3166-1 alpha-2 country code (case-insensitive), e.g. "DE", "us" */
  iso: string;
  className?: string;
}

/**
 * Renders a country flag SVG as an <img> element.
 *
 * The SVG is served from /public/flags/<iso>.svg – no bundling, the browser
 * fetches and caches each flag file individually.
 * Loading is deferred until the element is near the viewport using the shared
 * useIntersection hook (rootMargin: 200px look-ahead).
 *
 * Size is controlled entirely via CSS – no size prop needed.
 */
const CountryFlag = ({ iso, className }: CountryFlagProps) => {
  const normalized = iso.toLowerCase();
  const src = `/flags/${normalized}.svg`;

  const { intersectionRef, triggered } = useIntersection<HTMLSpanElement>({
    once: true,
    options: { rootMargin: '200px' },
  });

  return (
    <span
      ref={intersectionRef}
      className={`${styles.flag} ${className ?? ''}`}
      aria-hidden="true"
    >
      {triggered && (
        <img
          src={src}
          alt={`Flag-${iso}`}
          className={styles.img}
          draggable={false}
        />
      )}
    </span>
  );
};

export default CountryFlag;
